import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import MarkdownEditor from "../../MarkdownEditor/MarkdownEditor";

const EditContratClient = ({
  show,
  onClose,
  contrat,
  contractTypes = [],
  onSubmit,
  onDelete
}) => {
  const [typeId, setTypeId] = useState(contractTypes[0]?.id ?? "");
  const [expireDate, setExpireDate] = useState("");
  const [indetermine, setIndetermine] = useState(false);
  const [information, setInformation] = useState("");
  // Stores the last manually selected date so we can restore it if user unchecks "Indéterminée"
  const [previousDate, setPreviousDate] = useState("");

  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Determine if we are editing an existing contract or creating a new one
  const isEditing = !!contrat?.id;

  // Populate form fields when modal opens or the contract/contractTypes change
  useEffect(() => {
    if (!show) return;

    if (contrat) {
      // Extract only the date part (YYYY-MM-DD) from the ISO string
      const date = contrat?.expire_date ? contrat.expire_date.split("T")[0] : "";
      setTypeId(contrat?.type?.id ?? contractTypes[0]?.id ?? "");
      setExpireDate(date);
      setPreviousDate(date);
      // If no expire_date exists, treat it as indeterminate
      setIndetermine(!contrat?.expire_date);
      setInformation(contrat?.information || "");
    } else {
      // Reset all fields for a new contract
      setTypeId(contractTypes[0]?.id ?? "");
      setExpireDate("");
      setPreviousDate("");
      setIndetermine(false);
      setInformation("");
    }
  }, [contrat, show, contractTypes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await onSubmit({
        type: Number(typeId),
        date_expiration: indetermine ? null : (expireDate || null),
        information: information || null
      });

      if (isEditing) {
        toast.success("Contrat mis à jour avec succès !");
      } else {
        toast.success("Contrat ajouté avec succès !");
      }

      onClose();
    } catch (err) {
      toast.error(err.message || "Erreur lors de la sauvegarde du contrat");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);

    try {
      await onDelete();
      onClose();
    } catch (err) {
      toast.error(err.message || "Erreur suppression contrat");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditing ? "Modifier le contrat" : "Ajouter un contrat"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* CONTRACT TYPE selector */}
          <Form.Group className="mb-3">
            <Form.Label>Type de contrat</Form.Label>
            <Form.Select
              value={typeId}
              onChange={(e) => setTypeId(Number(e.target.value))}
              required
            >
              {contractTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* EXPIRY DATE + indeterminate checkbox */}
          <Form.Group className="mb-3">
            <Form.Label>Date de fin</Form.Label>
            <Form.Control
              type="date"
              value={expireDate}
              onChange={(e) => {
                // Update both the current date and the saved previous date
                setExpireDate(e.target.value);
                setPreviousDate(e.target.value);
              }}
              // Disable the date picker when indeterminate is checked
              disabled={indetermine}
            />
            <Form.Check
              className="mt-2"
              type="checkbox"
              label="Indéterminée"
              checked={indetermine}
              onChange={(e) => {
                const checked = e.target.checked;
                setIndetermine(checked);
                if (checked) {
                  // Clear the date field when switching to indeterminate
                  setExpireDate("");
                } else {
                  // Restore the previously selected date when unchecking
                  setExpireDate(previousDate);
                }
              }}
            />
          </Form.Group>

          {/* ADDITIONAL INFORMATION textarea */}
          <Form.Group>
            <Form.Label>Information</Form.Label>
            <MarkdownEditor
              value={information}
              onChange={setInformation}
              rows={3}
              placeholder="Informations complémentaires sur le contrat…"
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-between">
          {/* Show delete button only when editing an existing contract */}
          {isEditing && (
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={loading || deleting}
            >
              {deleting ? <Spinner size="sm" /> : "Supprimer"}
            </Button>
          )}

          <div>
            <Button
              variant="secondary"
              onClick={onClose}
              className="me-2"
              disabled={loading || deleting}
            >
              Annuler
            </Button>

            <Button
              variant="success"
              type="submit"
              disabled={loading || deleting}
            >
              {loading ? <Spinner size="sm"/> : isEditing ? "Enregistrer" : "Ajouter"}
            </Button>
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditContratClient;